/**
 * AddDbaInline — R4 (SPEC invoice-identity-cascade) — AC7.
 *
 * The inline "+ add a new DBA for this client" mini-form on the DBA row. Creates
 * a new DBA under the EXISTING client (R3's POST /{id}/add-dba, which never
 * mints a new organization — the Artigiano Howe pain), selects it, and stays in
 * the panel. Reachable in ≤2 taps (the "+ add" affordance is tap 1; Save is
 * tap 2).
 *
 * Plain-language labels guard Christa's known legal-vs-trade mix-up: the field
 * that prints on the invoice is labeled "Name shown on the invoice (the trade
 * name)" and the legal field is "Legal company name (optional)". No system
 * vocabulary (AC1). Address + recipients pre-fill from the client server-side
 * (R3), so the form stays to the two name fields she actually needs to type.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { assertNoSystemVocab } from './billToSourceTag';

export interface AddDbaInlineProps {
  clientName: string | null | undefined;
  /** Save handler — receives the two plain-language name fields. */
  onCreate: (args: { tradeName: string; legalName: string | null }) => void;
  onCancel: () => void;
  /** Disable while a create is in flight. */
  pending?: boolean;
}

// Plain-language labels (audited for system vocabulary at module load).
const TRADE_LABEL = 'Name shown on the invoice (the trade name)';
const LEGAL_LABEL = 'Legal company name (optional)';
assertNoSystemVocab(TRADE_LABEL, 'add-dba trade label');
assertNoSystemVocab(LEGAL_LABEL, 'add-dba legal label');

export function AddDbaInline({
  clientName,
  onCreate,
  onCancel,
  pending = false,
}: AddDbaInlineProps) {
  const [tradeName, setTradeName] = useState('');
  const [legalName, setLegalName] = useState('');

  const canSave = tradeName.trim().length > 0 && !pending;
  const client = clientName?.trim() || 'this client';

  return (
    <div
      className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3"
      data-testid="add-dba-inline"
    >
      <p className="text-xs text-slate-600">
        Adds a new DBA for {client}. The billing address and recipients carry
        over from {client} automatically.
      </p>
      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-700">
          {TRADE_LABEL}
        </label>
        <Input
          value={tradeName}
          onChange={(e) => setTradeName(e.target.value)}
          placeholder="e.g. Oak Bay Beach Hotel"
          autoFocus
          data-testid="add-dba-trade-name"
        />
      </div>
      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-700">
          {LEGAL_LABEL}
        </label>
        <Input
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          placeholder="e.g. 1234567 BC Ltd."
          data-testid="add-dba-legal-name"
        />
      </div>
      <div className="flex gap-2 justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          data-testid="add-dba-cancel"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!canSave}
          onClick={() =>
            onCreate({
              tradeName: tradeName.trim(),
              legalName: legalName.trim() || null,
            })
          }
          data-testid="add-dba-save"
        >
          Add DBA
        </Button>
      </div>
    </div>
  );
}
